import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  getLabels,
  getLabelsAdmin,
  createLabelAdmin,
  updateLabelAdmin,
  deleteLabelAdmin,
  getProjects,
  getProject,
  createProject,
  updateProject,
  updateProjectTemplates,
  updateProjectBillingTerms,
  deleteProject,
  transitionProject,
  mergeProjects,
  getProjectAssignments,
  createAssignment,
  updateAssignment,
  confirmAssignment,
  deleteAssignment,
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getProjectStatistics,
  getInvestmentSummary,
  getSubprojectStatistics,
  getTaskBoards,
  type ProjectFilters,
  type CreateProjectInput,
  type UpdateProjectInput,
  type UpdateProjectBillingTermsInput,
  type CreateAssignmentInput,
  type UpdateAssignmentInput,
  type MergeProjectsInput,
} from '../services/api/projects';
import type { UpdateProjectTemplatesInput } from '../services/api/templates';

// ===== Label Hooks =====

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: () => getLabels(),
  });
}

export function useLabelsAdmin() {
  return useQuery({
    queryKey: ['labelsAdmin'],
    queryFn: () => getLabelsAdmin(),
  });
}

export function useCreateLabel() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: { name: string; color?: string; description?: string; isSystem?: boolean }) => createLabelAdmin(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelsAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: (err) => onError(err, "Couldn't create label"),
  });
}

export function useUpdateLabel() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: { name?: string; color?: string | null; description?: string | null; isSystem?: boolean } }) =>
      updateLabelAdmin(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelsAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: (err) => onError(err, "Couldn't update label"),
  });
}

export function useDeleteLabel() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteLabelAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelsAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: (err) => onError(err, "Couldn't delete label"),
  });
}

// ===== Project Hooks =====

/** MKS-477 — the Tasks page's "All tasks" tab: readable projects with live tasks. */
export function useTaskBoards() {
  return useQuery({
    queryKey: ['taskBoards'],
    queryFn: getTaskBoards,
  });
}

export function useProjects(filters?: ProjectFilters) {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => getProjects(filters),
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => getProject(id!),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => onError(err, "Couldn't create project"),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProjectInput }) => updateProject(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
      // UAT 2026-05-13 — BalicekHeroCard inline edit (PATCH balicekHours /
      // balicekAmount / alertThresholds / overLimitAllowed) saved correctly
      // but the Hour Package card kept rendering pre-save numbers because
      // its consumption snapshot lives under a separate query key. Same for
      // OverageApprovalCard (monthly approvals list).
      queryClient.invalidateQueries({ queryKey: ['projectPackageStatus', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['projectMonthlyOverageApprovals', variables.id] });
    },
    onError: (err) => onError(err, "Couldn't update project"),
  });
}

/**
 * MKS-94 — save the project's billing terms through their own endpoint
 * (PATCH /api/projects/:id/billing-terms, admin | finance | project PM).
 */
export function useUpdateProjectBillingTerms() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProjectBillingTermsInput }) =>
      updateProjectBillingTerms(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
    },
    onError: (err) => onError(err, "Couldn't update billing terms"),
  });
}

/**
 * Phase 35 — Mutate project template assignments (TPL-04, TPL-05).
 *
 * Invalidates ['projects'] (list view) and ['project', id] (detail view)
 * on success so the new template IDs surface immediately to any consumer
 * that has the project cached.
 *
 * MKS-251 removed the TPL-04 lock, so this no longer answers 409
 * TEMPLATES_LOCKED. The project edit form writes the trio through
 * PATCH /api/projects/:id instead; this mutation remains for the admin-only
 * PATCH /api/projects/:id/templates route.
 *
 * Phase 40 (D-ENTRY) extends onSuccess to invalidate ['task-states', id] +
 * ['projectSlaTiers', id] so the SLA task auto-seed side effects surface
 * immediately. The SLA task auto-seed inserts new TaskState rows on first-time
 * SLA_TASK_TYPE_ID assignment inside the same backend $transaction; invalidating
 * both keys ensures the kanban board + SLA tiers admin reflect the new data
 * without a manual refetch.
 */
export function useUpdateProjectTemplates() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProjectTemplatesInput }) =>
      updateProjectTemplates(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
      // Phase 40 (D-ENTRY cache invalidation paragraph): SLA task auto-seed
      // inserts new TaskState rows on first-time SLA_TASK_TYPE_ID assignment
      // inside the same backend $transaction. Invalidate the consumers so
      // the kanban board + SLA tiers admin reflect the new data without a
      // manual refetch.
      queryClient.invalidateQueries({ queryKey: ['task-states', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['projectSlaTiers', variables.id] });
      // UAT 2026-05-13 — Migration Wizard step 3 persists balíček params +
      // coverage entries atomically with the template FKs. Invalidate the
      // Hour Package consumption + Monthly overage approvals + coverage
      // entry queries so the project detail surfaces fresh data instantly.
      queryClient.invalidateQueries({ queryKey: ['projectPackageStatus', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['projectMonthlyOverageApprovals', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['coverageEntries', variables.id] });
    },
    onError: (err) => onError(err, "Couldn't update project templates"),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => onError(err, "Couldn't delete project"),
  });
}

export function useTransitionProject() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: number; action: string; reason?: string }) =>
      transitionProject(id, action, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
    },
    onError: (err) => onError(err, "Couldn't transition project"),
  });
}

/**
 * Quick 260805-gmk — merge two projects (admin only). No onError toast: the
 * ProjectMergeModal catches rejections from `mutateAsync` directly so it can
 * render the 422 conflict list inline and keep the modal open (mirrors
 * `useReapplyProjectTemplate` in useProjectStates.ts).
 */
export function useMergeProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MergeProjectsInput) => mergeProjects(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ===== Assignment Hooks =====

export function useProjectAssignments(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectAssignments', projectId],
    queryFn: () => getProjectAssignments(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: number; input: CreateAssignmentInput }) =>
      createAssignment(projectId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectAssignments', variables.projectId] });
      // MKS-354 — a new assignment's role rate re-prices uninvoiced entries, so
      // both cards go stale too. The second key is a prefix: it has to match
      // every period / invoiceHistory variant of the builder query.
      queryClient.invalidateQueries({ queryKey: ['projectUninvoicedHours', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced', variables.projectId] });
    },
    onError: (err) => onError(err, "Couldn't create assignment"),
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ projectId, assignmentId, input }: { projectId: number; assignmentId: number; input: UpdateAssignmentInput }) =>
      updateAssignment(projectId, assignmentId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectAssignments', variables.projectId] });
      // MKS-354 — an assignment rate change re-prices uninvoiced entries, so
      // both cards go stale too. The second key is a prefix: it has to match
      // every period / invoiceHistory variant of the builder query.
      queryClient.invalidateQueries({ queryKey: ['projectUninvoicedHours', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced', variables.projectId] });
    },
    onError: (err) => onError(err, "Couldn't update assignment"),
  });
}

/**
 * MKS-358 — confirm one tentative assignment. Confirming is what grants the
 * person their project access, so the sidebar and the project itself go stale
 * with the assignment list.
 */
export function useConfirmAssignment() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ projectId, assignmentId }: { projectId: number; assignmentId: number }) =>
      confirmAssignment(projectId, assignmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectAssignments', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['sidebar'] });
    },
    onError: (err) => onError(err, "Couldn't confirm assignment"),
  });
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ projectId, assignmentId }: { projectId: number; assignmentId: number }) =>
      deleteAssignment(projectId, assignmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectAssignments', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      // MKS-354 — removing an assignment drops its role rate and re-prices the
      // same uninvoiced entries, so both cards go stale too. The second key is
      // a prefix: it has to match every period / invoiceHistory variant of the
      // builder query.
      queryClient.invalidateQueries({ queryKey: ['projectUninvoicedHours', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced', variables.projectId] });
    },
    onError: (err) => onError(err, "Couldn't delete assignment"),
  });
}

// ===== Customer Hooks =====

export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ['customers', search],
    queryFn: () => getCustomers(search),
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (name: string) => createCustomer(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => onError(err, "Couldn't create customer"),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => updateCustomer(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => onError(err, "Couldn't update customer"),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => onError(err, "Couldn't delete customer"),
  });
}

// ===== Statistics Hooks =====

export function useProjectStatistics(projectId: number | undefined) {
  return useQuery({
    queryKey: ['projectStatistics', projectId],
    queryFn: () => getProjectStatistics(projectId!),
    enabled: !!projectId,
  });
}

export function useInvestmentSummary(projectId: number | undefined) {
  return useQuery({
    queryKey: ['investmentSummary', projectId],
    queryFn: () => getInvestmentSummary(projectId!),
    enabled: !!projectId,
  });
}

export function useSubprojectStatistics(projectId: number | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['subprojectStatistics', projectId],
    queryFn: () => getSubprojectStatistics(projectId!),
    enabled: !!projectId && enabled,
  });
}
